// src/hooks/useServiceCategories.js
import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Custom hook for managing service categories
 * Handles adding, updating, and deleting service categories
 */
export function useServiceCategories() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const clearError = useCallback(() => setError(null), []);

  /**
   * Add a new service category
   * @param {Object} category - Category data { value, label, icon, translationKey }
   * @returns {Promise<Object>} Result with category and allCategories
   */
  const addCategory = useCallback(async (category) => {
    setLoading(true);
    setError(null);

    try {
      // Force all categories to use "danger" color
      const categoryWithColor = {
        ...category,
        color: 'danger'
      };

      const token = await user.getIdToken();
      const response = await fetch(
        `${FIREBASE_FUNCTIONS_BASE_URL}/services?operation=addCategory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ category: categoryWithColor })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to add category');
      }

      console.log('Successfully added category:', result.data);
      return result.data;
    } catch (err) {
      console.error('Error adding category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Update an existing service category
   * @param {string} categoryValue - The category value to update
   * @param {Object} updates - Fields to update { label, icon, translationKey }
   * @returns {Promise<Object>} Result with updated category and allCategories
   */
  const updateCategory = useCallback(async (categoryValue, updates) => {
    setLoading(true);
    setError(null);

    try {
      // Force all categories to use "danger" color
      const updatesWithColor = {
        ...updates,
        color: 'danger'
      };

      const token = await user.getIdToken();
      const response = await fetch(
        `${FIREBASE_FUNCTIONS_BASE_URL}/services?operation=updateCategory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ categoryValue, updates: updatesWithColor })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to update category');
      }

      console.log('Successfully updated category:', result.data);
      return result.data;
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Delete a service category
   * @param {string} categoryValue - The category value to delete
   * @returns {Promise<Object>} Result with success status, or error with affectedServices list
   */
  const deleteCategory = useCallback(async (categoryValue) => {
    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${FIREBASE_FUNCTIONS_BASE_URL}/services?operation=deleteCategory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ categoryValue })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete category');
      }

      // Check if the delete operation failed due to existing services
      if (result.data.success === false) {
        const err = new Error(result.data.error);
        err.affectedServices = result.data.affectedServices;
        throw err;
      }

      console.log('Successfully deleted category:', result.data);
      return result.data;
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    addCategory,
    updateCategory,
    deleteCategory,
    loading,
    error,
    clearError
  };
}
